class UsfColorSwatch {
  constructor(el) {
    this.el = el;

    this.selectors = {
      card: '.product-card',
      mainImg: '.product-card__image--main img',
      swatches: '.swatch-item',
      variantImg: '.color-swatch--variant-image',
    };

    this.hoverTracker = null;

    this.connected();
  }

  connected() {
    this.optionNodes = this.el.querySelectorAll(this.selectors.swatches);
    this.init();
  }

  init() {
    this.abortController = new AbortController();

    this.pcard = this.el.closest(this.selectors.card);
    this.mainImage = this.pcard?.querySelector(this.selectors.mainImg);

    this.optionNodes.forEach((button) => {
      button.addEventListener('mouseenter', this.onMouseEnter.bind(this), {
        signal: this.abortController.signal,
      });
    });
  }

  onMouseEnter(e) {
    const target = e.currentTarget;
    const variantImage = target.querySelector(this.selectors.variantImg);

    clearTimeout(this.hoverTracker);
    this.hoverTracker = setTimeout(() => {
      if (this.selected) this.selected.removeAttribute('aria-selected');
      target.setAttribute('aria-selected', true);
      this.selected = target;

      if (this.mainImage && variantImage) {
        this.mainImage.src = variantImage.src;
        this.mainImage.srcset = variantImage.srcset;
      }
    }, 50);
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }
}

class UsfProductForm {
    constructor(formEl) {
      this.form = formEl;
      this.productIdInput = formEl.querySelector('[name=id]');
      this.productIdInput.disabled = false;
  
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  
    get cartDrawerElement() {
      return document.querySelector('cart-drawer');
    }
  
    get submitButtonElement() {
      if (!this._submitButtonElement) {
        this._submitButtonElement = this.form.querySelector('[type="submit"]');
      }
      return this._submitButtonElement;
    }
  
    get isHideErrors() {
      return this.form.dataset.hideErrors === 'true';
    }
  
    handleFormSubmit(event) {
      if (FoxTheme.settings.cartType === 'page') return;
  
      event.preventDefault();
      if (this.submitButtonElement.getAttribute('aria-disabled') === 'true') return;
      this.lastSubmittedElement = event.submitter || event.currentTarget;
  
      this.displayFormErrors();
  
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );
  
      const config = FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
  
      this.formData = new FormData(this.form);
      this.formData.append('sections', sectionsToBundle);
      this.formData.append('sections_url', window.location.pathname);
      config.body = this.formData;
  
      this.submitButtonElement.setAttribute('aria-disabled', 'true');
      this.submitButtonElement.classList.add('btn--loading');
  
      this.handleFormSubmission(config);
    }
  
    displayFormErrors(errorMessage = false) {
      if (this.isHideErrors) return;
  
      this.formErrorMessage = this.formErrorMessage || this.form.querySelector('.product-form__error-message');
      if (!this.formErrorMessage) {
        if (errorMessage !== false) {
          alert(errorMessage);
        }
      } else {
        this.formErrorMessage.toggleAttribute('hidden', !errorMessage);
        if (errorMessage !== false) {
          this.formErrorMessage.innerText = errorMessage;
        }
      }
    }
  
    resetFormState() {
      this.hasError = false;
      this.submitButtonElement.removeAttribute('aria-disabled');
      this.displayFormErrors();
    }
  
    handleFormSubmission(config) {
      fetch(`${FoxTheme.routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(async parsedState => {
          if (parsedState.status) {
            this.handleCartError(parsedState);
            return;
          }
  
          const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
          cartJson['sections'] = parsedState['sections'];
  
          this.updateCartState(cartJson);
          this.dispatchProductAddedEvent(parsedState);
  
          this.showCartDrawer();
        })
        .catch(console.error)
        .finally(() => {
          this.submitButtonElement.classList.remove('btn--loading');
          if (!this.hasError) {
            this.submitButtonElement.removeAttribute('aria-disabled');
          }
        });
    }
  
    handleCartError(parsedState) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
        source: 'product-form',
        productVariantId: this.formData.get('id'),
        errors: parsedState.errors || parsedState.description,
        message: parsedState.message,
      });
  
      this.displayFormErrors(parsedState.description);
  
      document.dispatchEvent(
        new CustomEvent('product-ajax:error', {
          detail: {
            errorMessage: parsedState.description,
          },
        })
      );
  
      this.hasError = true;
    }
  
    updateCartState(cartJson) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    }
  
    dispatchProductAddedEvent(parsedState) {
      document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
          detail: {
            product: parsedState,
          },
        })
      );
    }
  
    showCartDrawer() {
      const quickViewModal = this.form.closest('quick-view-modal');
      if (quickViewModal) {
        if (this.cartDrawerElement && !this.cartDrawerElement.open) {
          document.body.addEventListener(
            quickViewModal.events.handleAfterHide,
            () => {
              setTimeout(() => {
                this.cartDrawerElement.show(this.lastSubmittedElement);
              });
            },
            { once: true }
          );
        }
  
        quickViewModal.hide(true);
      } else {
        this.cartDrawerElement?.show(this.lastSubmittedElement);
      }
    }
  }
  class UsfScrollingPromotion {
  constructor(el) {
    this.el = el;

    if (FoxTheme.config.motionReduced) return;

    this.promotion = this.el.querySelector('.promotion');
    this.repeatTimes = typeof this.el.dataset.repeats !== 'undefined' ? Number(this.el.dataset.repeats) : 10;

    FoxTheme.Motion.inView(this.el, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  init() {
    if (this.el.childElementCount === 1) {
      this.promotion.classList.add('promotion--animated');

      for (let index = 0; index < this.repeatTimes; index++) {
        this.clone = this.promotion.cloneNode(true);
        this.el.appendChild(this.clone);
      }

      // Pause when out of view.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.scrollingPlay();
            } else {
              this.scrollingPause();
            }
          });
        },
        { rootMargin: '0px 0px 50px 0px' }
      );

      this.observer.observe(this.el);
    }
  }

  scrollingPlay() {
    this.el.classList.remove('scrolling-promotion--paused');
  }

  scrollingPause() {
    this.el.classList.add('scrolling-promotion--paused');
  }

  disconnect() {
    this.observer?.disconnect();
  }
}



class UsfCompareCheckbox {
  constructor(el) {
    this.el = el;
    this.checkboxEl = this.el.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);

    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  init() {
    const { productId } = this.checkboxEl.dataset;
    this.checkboxEl.checked = CompareUtils.isProductInList(productId);
  }

  onChange(evt) {
    evt.preventDefault();

    const { productId, productUrl } = this.checkboxEl.dataset;

    if (this.checkboxEl.checked) {
      const isAdded = CompareUtils.addToList(productId, productUrl);
      if (!isAdded) {
        this.checkboxEl.checked = false;
      }
    } else {
      CompareUtils.removeFromList(productId);
    }
  }

  destroy() {
    this.checkboxEl.removeEventListener('change', this.onChangeHandler);
  }
}


